import { DEV_URL, rget } from 'utils/requests';

const COMPLAINTS_BASE_URL = `${DEV_URL}/api/Complaints`;

let sharedFiltersCache = null;
let sharedFiltersRequest = null;

const buildUrl = (path, params = {}) => {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, String(value));
    }
  });

  const queryString = query.toString();
  return `${COMPLAINTS_BASE_URL}${path}${queryString ? `?${queryString}` : ''}`;
};

const ensureResponse = async (responsePromise) => {
  const response = await responsePromise;

  if (!response || response.status < 200 || response.status >= 300) {
    throw new Error(response?.data || 'Complaints request failed');
  }

  return response.data;
};

const palette = ['#de6666', '#f6a04a', '#748fc4', '#59b27c', '#7c8db5', '#9bc53d'];
const roundCount = (value) => Math.round(Number(value || 0));
const toNullableCount = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? Math.round(num) : null;
};
const toIsoDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
};

export const getComplaintFilterOptions = async (params = {}) => {
  const hasQueryParams = Object.values(params || {}).some((value) => value !== undefined && value !== null && value !== '');

  if (!hasQueryParams && sharedFiltersCache) {
    return sharedFiltersCache;
  }

  if (!hasQueryParams && sharedFiltersRequest) {
    return sharedFiltersRequest;
  }

  const request = ensureResponse(rget(buildUrl('/filters', params)));

  if (!hasQueryParams) {
    sharedFiltersRequest = request
      .then((response) => {
        sharedFiltersCache = response;
        return response;
      })
      .finally(() => {
        sharedFiltersRequest = null;
      });

    return sharedFiltersRequest;
  }

  return request;
};

export const getComplaintOverview = async (params) => {
  const data = await ensureResponse(rget(buildUrl('/overview', params)));
  const summary = data?.summary || {};
  const resolvedClassValue = summary.mostAffectedClass || params.customerClass || 'Selected class';
  const takeaways = (data?.takeaways || []).map((item) => ({
    kind: item.kind,
    emphasis: item.emphasis,
    text:
      item.kind === 'State'
        ? `${summary.supplierName || params.brandName || 'Selected brand'} record concentrated in ${item.primaryValue}`
        : item.kind === 'Type'
          ? `${item.primaryValue} dominates the record`
          : `${resolvedClassValue} record should be reviewed more closely`,
  }));

  return {
    filters: {
      brandId: params.brandId,
      brandName: params.brandName || '',
      windowMonths: 0,
      compareMonths: 0,
      fromMonth: params.fromMonth || null,
      toMonth: params.toMonth || null,
      compareFromMonth: params.compareFromMonth || null,
      compareToMonth: params.compareToMonth || null,
      customerClass: params.customerClass || 'All',
    },
    summary: {
      t12mComplaints: roundCount(summary.currentComplaintCount),
      priorComplaints: roundCount(summary.priorComplaintCount),
      latestAvailableMonth: toIsoDate(summary.latestAvailableMonth),
      currentStartMonth: toIsoDate(summary.currentStartMonth),
      currentEndMonth: toIsoDate(summary.currentEndMonth),
      priorStartMonth: toIsoDate(summary.priorStartMonth),
      priorEndMonth: toIsoDate(summary.priorEndMonth),
      topState: summary.topState || '-',
      topStateNote: 'largest footprint',
      topComplaintType: summary.topComplaintType || '-',
      topComplaintTypeNote: 'persistent',
      mostAffectedClass: summary.mostAffectedClass || 'All',
      mostAffectedClassNote: summary.mostAffectedClass ? 'where available' : 'selected filter context',
    },
    byState: (data?.byState || []).map((item, index) => ({ label: item.label, value: roundCount(item.value), color: palette[index % palette.length] })),
    byType: (data?.byType || []).map((item, index) => ({
      label: item.label,
      value: roundCount(item.value),
      color: palette[(index + 1) % palette.length],
      priorityName: item.priorityName || null,
      priorityScore: Number(item.priorityScore || 0),
    })),
    stateComparison: (data?.stateComparison || []).map((item, index) => ({
      label: item.label,
      deltaPct: Number(item.deltaPct || 0),
      color: Number(item.deltaPct || 0) < 0 ? '#28a745' : index === 0 ? '#d9534f' : '#f0ad4e',
    })),
    takeaways,
  };
};

export const getComplaintPeers = async (params) => {
  const data = await ensureResponse(rget(buildUrl('/peers', params)));
  const summary = data?.summary || {};
  const selected = roundCount(summary.selectedCount);
  const median = roundCount(summary.medianCount);

  const selectedLabel = (summary.selectedSupplier || params.brandName || '').trim();

  const chartRows = (data?.chart || []).map((item) => ({
    label: item.label,
    value: roundCount(item.value),
  }));

  const dedupedChartRows = Array.from(
    chartRows.reduce((acc, item) => {
      if (!acc.has(item.label)) {
        acc.set(item.label, item);
      }
      return acc;
    }, new Map()).values()
  );

  const rankedPeers = [...dedupedChartRows]
    .filter((item) => item.label !== 'Market median')
    .sort((a, b) => a.value - b.value || a.label.localeCompare(b.label));

  const selectedIndex = rankedPeers.findIndex((item) => item.label === selectedLabel);
  const selectedPeer = selectedIndex >= 0 ? rankedPeers[selectedIndex] : { label: selectedLabel || '-', value: selected };
  const lowestPeer = rankedPeers[0] || { label: '-', value: 0 };
  const highestPeer = rankedPeers[rankedPeers.length - 1] || { label: '-', value: 0 };
  const highestValue = roundCount(highestPeer.value);

  const peersWithoutSelected = rankedPeers.filter((item) => item.label !== selectedPeer.label);
  const nearestLower = [...peersWithoutSelected]
    .filter((item) => item.value < selected)
    .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label))[0] || null;
  const nearestHigher = [...peersWithoutSelected]
    .filter((item) => item.value > selected)
    .sort((a, b) => a.value - b.value || a.label.localeCompare(b.label))[0] || null;

  let peerA = nearestLower;
  let peerB = nearestHigher;

  if (!peerA && !peerB) {
    peerA = peersWithoutSelected[0] || null;
    peerB = peersWithoutSelected[1] || null;
  } else if (!peerA) {
    peerA = peerB;
    peerB = peersWithoutSelected.find((item) => item.label !== (peerA?.label || '')) || null;
  } else if (!peerB) {
    peerB = peerA;
    peerA = [...peersWithoutSelected].reverse().find((item) => item.label !== (peerB?.label || '')) || peerA;
  }

  if (peerA && peerB && peerA.label === peerB.label) {
    peerB = peersWithoutSelected.find((item) => item.label !== peerA.label) || null;
  }

  const fallbackPeer = { label: '-', value: 0 };
  const displayPeerA = peerA || fallbackPeer;
  const displayPeerB = peerB || fallbackPeer;

  const nearestBetter = selectedIndex > 0 ? rankedPeers[selectedIndex - 1] : null;
  const nearestWorse = selectedIndex >= 0 && selectedIndex < rankedPeers.length - 1 ? rankedPeers[selectedIndex + 1] : null;

  const medianRow = dedupedChartRows.find((row) => row.label === 'Market median')
    || { label: 'Market median', value: median };

  const chartWithMarkers = rankedPeers.map((row) => {
    let marker = '';
    if (row.label === lowestPeer.label && lowestPeer.label !== '-') marker = 'Best';
    if (row.label === highestPeer.label && highestPeer.label !== '-') marker = 'Worst';
    if (nearestBetter && row.label === nearestBetter.label) marker = marker ? `${marker} / Me-1` : 'Me-1';
    if (nearestWorse && row.label === nearestWorse.label) marker = marker ? `${marker} / Me+1` : 'Me+1';
    if (row.label === selectedPeer.label && selectedPeer.label !== '-') marker = marker ? `${marker} / Me` : 'Me';
    const color =
      row.label === selectedPeer.label
        ? '#59b27c'
        : row.label === highestPeer.label
          ? '#de6666'
          : '#f6a04a';
    return { ...row, marker, color };
  });

  const orderedChart = [
    ...chartWithMarkers.sort((a, b) => a.value - b.value || a.label.localeCompare(b.label)),
    { ...medianRow, marker: 'Median', color: '#748fc4' },
  ];

  const buildPeerNote = (peerValue, selectedValue) => {
    if (!peerValue || peerValue <= 0 || !selectedValue || selectedValue <= 0) return 'No peer data';
    const pct = Math.round((Math.abs(peerValue - selectedValue) / Math.max(selectedValue, 1)) * 1000) / 10;
    if (peerValue > selectedValue) return `${pct}% above selected`;
    if (peerValue < selectedValue) return `${pct}% below selected`;
    return 'same as selected';
  };

  return {
    filters: {
      brand: params.brandName || '',
      state: params.stateCode,
      metric: params.metric,
      windowMonths: 0,
      fromMonth: params.fromMonth || null,
      toMonth: params.toMonth || null,
    },
    summary: {
      brandCount: selected,
      brandUnit: 'raw',
      latestAvailableMonth: toIsoDate(summary.latestAvailableMonth),
      currentStartMonth: toIsoDate(summary.currentStartMonth),
      currentEndMonth: toIsoDate(summary.currentEndMonth),
      lowestPeerLabel: displayPeerA.label || '-',
      lowestPeerCount: roundCount(displayPeerA.value),
      lowestPeerNote: displayPeerA.label === '-' ? 'No peer data' : buildPeerNote(roundCount(displayPeerA.value), selected),
      highestPeerLabel: displayPeerB.label || '-',
      highestPeerCount: roundCount(displayPeerB.value),
      highestPeerNote: displayPeerB.label === '-' ? 'No peer data' : buildPeerNote(roundCount(displayPeerB.value), selected),
      marketMessage: selected <= median ? 'Cleaner than peers' : selected >= highestValue * 0.75 ? 'More exposed than peers' : 'Middle of peer pack',
      marketMessageNote: selected <= median ? 'defensible' : selected >= highestValue * 0.75 ? 'watchable' : 'contextual',
    },
    chart: orderedChart,
    insights: (data?.insights || []).map((item) => ({ text: item.text, kind: item.kind, emphasis: item.emphasis })),
  };
};

export const getComplaintIntelligence = async (params, filterOptions = null) => {
  const data = await ensureResponse(rget(buildUrl('/intelligence', params)));
  const summary = data?.summary || {};
  const heatmapRows = data?.heatmap || [];
  const complaintTypes = filterOptions?.complaintTypes?.length
    ? filterOptions.complaintTypes.map((item) => (typeof item === 'string' ? item : item.complaintName))
    : [...new Set(heatmapRows.map((item) => item.complaintName))];
  const states = filterOptions?.states?.length
    ? filterOptions.states
    : [...new Set(heatmapRows.map((item) => item.stateCode))];

  const highestPriorityType = summary.highestPriorityType || '';
  const normalizedHighestType = highestPriorityType.trim().toLowerCase();
  const topIssueCluster = summary.topIssueCluster || '';
  const clusterTokens = topIssueCluster.trim().split(/\s+/);
  const clusterStateCode = clusterTokens[0] || '';
  const clusterComplaintName = clusterTokens.slice(1).join(' ').trim();
  const normalizedClusterName = clusterComplaintName.toLowerCase();
  const highestPriorityTypeCountDisplay = toNullableCount(
    heatmapRows
      .filter((item) => String(item.complaintName || '').trim().toLowerCase() === normalizedHighestType)
      .reduce((total, item) => total + Number(item.complaintCount || 0), 0)
  );

  const topIssueClusterCountDisplay = toNullableCount(
    heatmapRows.find((item) =>
      String(item.stateCode || '').trim().toUpperCase() === clusterStateCode.toUpperCase()
      && String(item.complaintName || '').trim().toLowerCase() === normalizedClusterName
    )?.complaintCount
  );

  return {
    filters: {
      brandId: params.brandId,
      brandName: params.brandName || '',
      windowMonths: 0,
      compareMonths: 0,
      fromMonth: params.fromMonth || null,
      toMonth: params.toMonth || null,
      compareFromMonth: params.compareFromMonth || null,
      compareToMonth: params.compareToMonth || null,
      customerClass: params.customerClass || 'All',
    },
    summary: {
      complaints: toNullableCount(summary.complaintCount),
      complaintsNote: 'context only',
      latestAvailableMonth: toIsoDate(summary.latestAvailableMonth),
      currentStartMonth: toIsoDate(summary.currentStartMonth),
      currentEndMonth: toIsoDate(summary.currentEndMonth),
      priorStartMonth: toIsoDate(summary.priorStartMonth),
      priorEndMonth: toIsoDate(summary.priorEndMonth),
      statesWithIssues: toNullableCount(summary.statesWithIssues),
      statesWithIssuesNote: 'active',
      priorityType: summary.highestPriorityType || '-',
      priorityTypeNote: highestPriorityTypeCountDisplay === null ? '' : `${highestPriorityTypeCountDisplay} complaints`,
      issueCluster: summary.topIssueCluster || '-',
      issueClusterNote: topIssueClusterCountDisplay === null ? '' : `${topIssueClusterCountDisplay} complaints`,
    },
    heatmap: {
      states,
      types: complaintTypes,
      cells: heatmapRows.map((item) => ({
        state: item.stateCode,
        type: item.complaintName,
        count: toNullableCount(item.complaintCount),
        weightedCount: toNullableCount(item.weightedCount),
        priorityName: item.priorityName || null,
        priorityScore: Number(item.priorityScore || 0),
        intensity: toNullableCount(item.complaintCount) === 0
          ? 'none'
          : ((item.severity || 'None').toLowerCase() === 'none' ? 'none' : (item.severity || 'medium').toLowerCase()),
      })),
    },
    issueQueue: (data?.issueQueue || []).map((item) => ({
      text: `${item.stateCode} ${item.complaintName} showing ${toNullableCount(item.complaintCount) ?? ''} complaints in the selected window`,
      weightedCount: toNullableCount(item.weightedCount),
      kind: item.recommendedAction === 'Escalate' ? 'High' : item.recommendedAction === 'Review' ? 'Medium' : 'Low',
      emphasis: item.recommendedAction,
    })),
    issueComparison: (data?.issueComparison || []).map((item) => ({
      label: item.label,
      deltaPct: Number(item.deltaPct || 0),
      color: Number(item.deltaPct || 0) > 0 ? '#d9534f' : Number(item.deltaPct || 0) < 0 ? '#28a745' : '#748fc4',
    })),
  };
};
